import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  DialogFooter,
  ScrollArea,
  Text,
} from "@salestrak/ui";

import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { FormFillCardsSkeleton } from "#app/components/forms/form-skeletons";
import {
  FormFillBody,
  FormFillColumn,
  FormStepRail,
  FORM_SUBMIT_MS,
  FormSubmitOutcome,
  FormTitleCard,
  cantSubmitToast,
} from "#app/components/forms/form-fill/form-fill-body";
import {
  type AnswerUpdate,
  FormPhotoUploadsProvider,
  useFormPhotoUploads,
} from "#app/components/forms/form-fill/form-photo-uploads";
import {
  type Form,
  formSteps,
  nextAskedStep,
  offersResubmit,
  startingAnswers,
  type SubmissionErrors,
  submitLabelOf,
  validateSubmission,
  walkForm,
} from "#app/lib/forms";

export function FormPreview({
  form,
  customFieldsById,
  loading = false,
  status,
}: {
  form: Pick<
    Form,
    "name" | "description" | "bannerUrl" | "bannerMediaId" | "items" | "settings"
  >;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  loading?: boolean;
  status: string | null;
}) {
  const [answers, setAnswers] = useState(() => startingAnswers(form.items, customFieldsById));
  const [errors, setErrors] = useState<SubmissionErrors>({});
  const [phase, setPhase] = useState<"filling" | "submitting" | "submitted">("filling");
  const updateAnswer = useCallback<AnswerUpdate>((itemId, next) => {
    setAnswers((prev) => ({ ...prev, [itemId]: next(prev[itemId]) }));
    setErrors((prev) => {
      if (!(itemId in prev)) return prev;
      const { [itemId]: _, ...rest } = prev;
      return rest;
    });
  }, []);
  const photoUploads = useFormPhotoUploads(updateAnswer);
  const steps = useMemo(() => formSteps(form), [form]);
  const [history, setHistory] = useState<number[]>([0]);
  const paged = steps.length > 1;
  const index = Math.min(history.at(-1) ?? 0, steps.length - 1);
  const walk = useMemo(
    () => walkForm({ items: steps.flatMap((step) => step.items) }, answers, customFieldsById),
    [answers, customFieldsById, steps],
  );
  const lastPage = nextAskedStep(steps, index, walk.hidden) === "submit";
  const pageItems = useMemo(
    () => (paged ? (steps[index]?.items ?? []) : form.items),
    [form.items, index, paged, steps],
  );
  useEffect(() => {
    if (phase !== "submitting") return;
    const timer = setTimeout(() => setPhase("submitted"), FORM_SUBMIT_MS);
    return () => clearTimeout(timer);
  }, [phase]);
  const onSubmit = () => {
    const found = validateSubmission(walk.items, answers, customFieldsById);
    setErrors(found);
    const held = walk.items.filter((item) => item.itemId in found);
    const [first] = held;
    if (first) {
      cantSubmitToast(held, found);
      const page = steps.findIndex((step) => step.items.some((item) => item.itemId === first.itemId));
      if (paged && page >= 0 && page !== index) setHistory((current) => [...current, page]);
      return;
    }
    setPhase("submitting");
  };
  const startOver = () => {
    setAnswers(startingAnswers(form.items, customFieldsById));
    setErrors({});
    setHistory([0]);
    setPhase("filling");
  };
  if (phase !== "filling") {
    return (
      <Box background="sunken" direction="column" fill="flex" minHeight={0}>
        <FormSubmitOutcome
          actions={
            <Button onClick={startOver} variant="outline">
              {offersResubmit(form) ? "Submit another" : "Start over"}
            </Button>
          }
          form={form}
          phase={phase}
          submitted={1}
        />
        <PreviewFooter />
      </Box>
    );
  }
  return (
    <Box background="sunken" direction="column" fill="flex" minHeight={0}>
    {/* Size container the bottom gutter measures (`50cqh`). */}
      <ScrollArea className="[container-type:size]">
        <Box
          className="pb-[50cqh]"
          direction="column"
          duration={100}
          enter="fade"
          paddingTop={{ base: "lg", sm: "xl" }}
          paddingX={{ base: "lg", sm: "xl" }}
        >
          <FormFillColumn>
            {status ? (
              <Text caps size="2xs" variant="faint" weight="medium">
                {status}
              </Text>
            ) : null}
            <FormTitleCard form={form} />
            {loading ? (
              <FormFillCardsSkeleton count={pageItems.length} />
            ) : (
              <FormPhotoUploadsProvider value={photoUploads}>
                <FormFillBody
                  answers={answers}
                  customFieldsById={customFieldsById}
                  errors={errors}
                  form={{ items: pageItems }}
                  hidden={walk.hidden}
                  onAnswerChange={(itemId, value) => updateAnswer(itemId, () => value)}
                />
              </FormPhotoUploadsProvider>
            )}
          </FormFillColumn>
        </Box>
      </ScrollArea>
      <DialogFooter className="in-data-[slot=drawer-popup]:mt-0!">
        <Box align="center" direction="row" gap="sm" grow>
          <PreviewNote />
          {paged ? (
            <FormStepRail
              done={history.length - 1}
              left={walk.path.length - Math.max(walk.path.indexOf(index), 0)}
            />
          ) : null}
          <Box grow />
          {history.length > 1 ? (
            <Button onClick={() => setHistory((current) => current.slice(0, -1))} variant="ghost">
              Back
            </Button>
          ) : null}
          {lastPage ? (
            <Button onClick={onSubmit}>{submitLabelOf(form)}</Button>
          ) : (
            <Button
              onClick={() =>
                setHistory((current) => {
                  const next = nextAskedStep(steps, index, walk.hidden);
                  return next === "submit" ? current : [...current, next];
                })
              }
            >
              Next
            </Button>
          )}
        </Box>
      </DialogFooter>
    </Box>
  );
}

function PreviewNote() {
  return (
    <Text size="xs" truncate={1} variant="muted">
      Preview — nothing is saved
    </Text>
  );
}

function PreviewFooter() {
  return (
    <DialogFooter className="in-data-[slot=drawer-popup]:mt-0!">
      <Box align="center" direction="row" grow minHeight={36}>
        <PreviewNote />
      </Box>
    </DialogFooter>
  );
}
